import * as React from 'react';
import styled from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';
import { ensureArray } from '@agilepoint/utils-array';
import { IDropdownProps, IDropdownOption, OptionType } from './dropdown-props';
import { IItemRenderProps } from '../listview/listview-types';
import { DropdownField, OptionState, Options, DropdownItem, DropdownArrow, DropdownValue } from './dropdown-parts';
import { excludeKeys, ListIterator, KeyStrokeListener } from '../../utils';
import { Spinner } from '../spinner';

interface IDropDownState {
    selectedKeys: React.Key[];
    searchText: string;
    showOptions: boolean;
    showSelectAll: boolean;
    canFocusOptions: boolean;
    itemsPool: any[];
}

enum KeysToListen {
    enter = 'Enter',
    arrowUp = 'ArrowUp',
    arrowDown = 'ArrowDown'
}

const SELECT_ALL_KEY = 'SELECT_ALL';

const DropdownWrapper = styled.div.attrs(() => ({
    className: String('ap-dropdown-wrapper')
}))`
    display: inline-block;
    min-width: 150px;
    height: 40px;
`;

function getOptionState(optionProps: IItemRenderProps) {
    const { isFocused, isSelected } = optionProps;
    if (isFocused && isSelected) {
        return OptionState.selectedAndFocused;
    }
    if (isFocused) {
        return OptionState.focused;
    }
    if (isSelected) {
        return OptionState.selected;
    }
    return OptionState.default;
}

export class Dropdown extends React.PureComponent<IDropdownProps, IDropDownState> {

    static displayName = 'Dropdown';

    private dropDownFieldRef: React.RefObject<HTMLDivElement>;
    private searchFieldRef = React.createRef<HTMLInputElement>();
    private optionsRendererRef = React.createRef<HTMLDivElement>();
    private keyStroke: KeyStrokeListener;
    private get optionsMap() {
        return this.getOptionsMap(this.state.itemsPool);
    }

    private optionsGroupMap = new Map<React.Key, IDropdownOption>();
    private itemsIterator: ListIterator<any>;

    private getItemsWithSelectAll = memoizeFunction((selectAllLabel: string, itemsToDisplay: IDropdownOption[]) => [
        { text: selectAllLabel, key: SELECT_ALL_KEY, type: OptionType.SelectAll },
        ...itemsToDisplay
    ]);

    private getOptionsMap = memoizeFunction((itemsPool: any[]) => {
        const optionsMap = new Map<React.Key, IDropdownOption>();
        itemsPool.forEach(item => {
            const isOptionHeader = item.type === OptionType.OptionGroup;
            if (isOptionHeader) {
                this.optionsGroupMap.set(item.key, item);
                return;
            }
            optionsMap.set(item.key, item);
        });
        return optionsMap;
    }, 2);

    static getDerivedStateFromProps(props: IDropdownProps) {
        const { selectedKeys, disabled, items } = props;
        const newState: Partial<IDropDownState> = {};

        if (items !== undefined) {
            newState.itemsPool = items;
        }

        if (selectedKeys !== undefined) {
            newState.selectedKeys = ensureArray(selectedKeys);
        }

        if (disabled) {
            newState.showOptions = false;
        }

        return Object.keys(newState).length ? newState : null;
    }

    constructor(props: IDropdownProps) {
        super(props);
        const { defaultSelectedKeys = [], showSelectAll = false, items, initialItems, elementRef, itemsIterator } = props;
        const selectedKeys = ensureArray(props.selectedKeys || defaultSelectedKeys);
        const listItems = items ? items : (initialItems || []);
        this.dropDownFieldRef = React.createRef<HTMLDivElement>();
        this.itemsIterator = itemsIterator || new ListIterator(listItems);
        this.dropDownFieldRef = elementRef || React.createRef<HTMLDivElement>();
        this.keyStroke = new KeyStrokeListener(this.dropDownFieldRef);
        this.state = {
            showOptions: false,
            selectedKeys,
            searchText: '',
            showSelectAll,
            canFocusOptions: true,
            itemsPool: listItems
        };
    }

    render() {
        const { hidden } = this.props;
        const dropdownProps = excludeKeys(this.props, 'defaultSelectedKeys', 'onSearchTextChange',
            'error', 'fitOptionWidthToFieldWidth', 'multiSelect', 'items', 'searchPlaceHolder', 'selectedKeys', 'separator', 'loading',
            'showSelectAll', 'showSearchBox', 'selectAllLabel', 'onRenderItem', 'onValueChange', 'initialItems', 'loadOnDemand', 'onFetchMoreItems');
        return hidden ? null : (
            <DropdownWrapper {...dropdownProps}>
                {this.renderDropDownField()}
                {this.renderOptions()}
            </DropdownWrapper>
        );
    }

    componentDidMount() {
        this.keyStroke.subscribe(this._onDropdownSelection, 'Enter', ' ');
        this.keyStroke.subscribe(this._onDropdownClose, 'Escape');
    }

    componentDidUpdate(prevProps: IDropdownProps) {
        const { multiSelect } = this.props;
        const { selectedKeys, canFocusOptions } = this.state;
        const { current: searchField } = this.searchFieldRef;
        const { current: options } = this.optionsRendererRef;
        if (multiSelect !== prevProps.multiSelect) {
            this.setState({
                selectedKeys: multiSelect ? selectedKeys : selectedKeys.slice(0, 1)
            });
        }

        if (!canFocusOptions) {
            return;
        }

        if (searchField) {
            searchField.focus();
        }
        else if (options) {
            options.focus();
        }
    }

    componentWillUnmount() {
        this.keyStroke.unsubscribe();
    }

    private renderItem = (itemProps: IItemRenderProps) => {
        const { multiSelect = false, onRenderItem, showSelectAll = false, itemHeight = 35 } = this.props;
        const { showSelectAll: noFilter, selectedKeys } = this.state;
        const { data: { type, key } } = itemProps;
        const isSelected = type === OptionType.SelectAll ? this.isAllSelected(selectedKeys) : selectedKeys.includes(key);

        const renderContent = () => (
            <DropdownItem
                multiSelect={multiSelect}
                isGrouped={this.optionsGroupMap.size > 0}
                showSelectAll={multiSelect && showSelectAll && noFilter}
                itemProps={itemProps}
                itemHeight={itemHeight}
                onSelectAllChange={this._onSelectAllChange}
                optionState={getOptionState(itemProps)}
                optionType={type}
                isOptionSelected={isSelected}
                onCloseWithoutChange={this._onCloseWithoutChange} />
        );

        if (onRenderItem) {
            return onRenderItem({ ...itemProps, isSelected, renderDefault: renderContent, toggleSelectAll: this._onSelectAllChange });
        }
        return renderContent();
    }

    private onDemandFetch = async () => {
        const { onFetchMoreItems, loadOnDemand } = this.props;
        const { itemsPool } = this.state;
        if (!loadOnDemand || onFetchMoreItems === undefined) {
            return null;
        }

        const newItems = await onFetchMoreItems();
        if (newItems === null || newItems.length === 0) {
            return null;
        }

        const itemsToDisplay = itemsPool.concat(newItems);
        this.setState({ itemsPool: itemsToDisplay });
        return newItems;
    }

    private renderDropDownField(): React.ReactNode {
        const value = this.getDropdownValueToDisplay();
        const { disabled, error, loading } = this.props;
        const { showOptions } = this.state;
        return (
            <DropdownField
                alignItems="center"
                justifyContent="space-between"
                value={value}
                ref={this.dropDownFieldRef}
                $disabled={disabled}
                $error={error}
                $showOptions={showOptions}
                onClick={this._onClick}>
                {this.renderDropDownValue()}
                {
                    loading ? <Spinner size="small" spinnerType="indeterminate" /> :
                        <DropdownArrow iconName="ap-up-arrow-thin.outline" iconSize="12" $showOptions={showOptions} $disabled={disabled} $error={error} />
                }
            </DropdownField>
        );
    }

    private renderDropDownValue() {
        const { placeholder = 'Select' } = this.props;
        let dropDownValue = this.getDropdownValueToDisplay();
        dropDownValue = dropDownValue ? dropDownValue : placeholder;
        return (
            <DropdownValue>
                {dropDownValue}
            </DropdownValue>
        );
    }

    private getItemsToRender(): IDropdownOption[] {
        const { showSelectAll, selectAllLabel = 'Select All', multiSelect } = this.props;
        const { showSelectAll: renderSelectAll, searchText, itemsPool } = this.state;
        const lowerCaseSearchText = searchText.toLowerCase();
        const itemsToDisplay = searchText ? itemsPool.filter(item => item.type !== OptionType.OptionGroup && item.text.toLowerCase().includes(lowerCaseSearchText)) : itemsPool;
        return renderSelectAll && showSelectAll && multiSelect
            ? this.getItemsWithSelectAll(selectAllLabel, itemsToDisplay)
            : itemsToDisplay;
    }

    private onEnterKeyPress = (item: IDropdownOption) => {
        const { multiSelect } = this.props;
        const { selectedKeys } = this.state;
        let newSelectedKeys = multiSelect ? selectedKeys.slice() : [];
        const selectedKey = item.key;
        if (selectedKey === SELECT_ALL_KEY) {
            newSelectedKeys = !this.isAllSelected(selectedKeys) ? Array.from(this.optionsMap.keys()) : [];
        }
        else if (selectedKeys.includes(selectedKey)) {
            newSelectedKeys = selectedKeys.filter(key => selectedKey !== key);
        }
        else {
            newSelectedKeys.push(selectedKey);
        }
        this._onChangeInList(newSelectedKeys);
    }

    private handleKeyboardNavigationOnSearch = (ev: React.KeyboardEvent) => {
        const { arrowDown, arrowUp, enter } = KeysToListen;
        ev.stopPropagation();
        switch (ev.key) {
            case arrowDown:
                this.itemsIterator.next();
                break;
            case arrowUp:
                this.itemsIterator.prev();
                break;
            case enter:
                this.onEnterKeyPress(this.itemsIterator.getValue());
                break;
            default:
                break;
        }
    }

    private renderOptions(): React.ReactNode | null {
        const {
            showSelectAll = false, disabled,
            multiSelect = false, searchPlaceHolder = 'Search',
            fitOptionWidthToFieldWidth = true, itemHeight = 32,
            loadOnDemand, hideSearchIcon, disableRefocusTarget
        } = this.props;
        const { showOptions, showSelectAll: renderSelectAll, selectedKeys } = this.state;
        const itemToDisplay = this.getItemsToRender();
        this.itemsIterator.updateList(itemToDisplay);
        return disabled ? null :
            (
                <Options
                    elementRef={this.optionsRendererRef}
                    items={itemToDisplay}
                    itemHeight={itemHeight}
                    renderSelectAll={showSelectAll && renderSelectAll && multiSelect}
                    onChange={this._onChangeInList}
                    onRenderItem={this.renderItem}
                    targetRef={this.dropDownFieldRef}
                    hidden={!showOptions}
                    disableRefocusTarget={disableRefocusTarget}
                    useTargetsWidth
                    onDismiss={this.onDismiss}
                    animateOnOpen={false}
                    searchPlaceHolder={searchPlaceHolder}
                    onSearchValueChange={this._onFilterValueChange}
                    searchFieldRef={this.searchFieldRef}
                    showSearchField={this.shouldRenderSearchField()}
                    selectable={multiSelect ? 'multiple' : 'single'}
                    selectedKeys={selectedKeys}
                    canSelectItem={this.canSelectItem}
                    fitOptionWidthToFieldWidth={fitOptionWidthToFieldWidth}
                    itemsIterator={this.itemsIterator}
                    handleKeyboardNavigationOnSearch={this.handleKeyboardNavigationOnSearch}
                    loadOnDemand={loadOnDemand}
                    onFetchMoreItems={this.onDemandFetch}
                    hideSearchIcon={hideSearchIcon} />
            );
    }

    private shouldRenderSearchField() {
        const { showSearchBox } = this.props;
        const { itemsPool } = this.state;
        return showSearchBox === undefined ? itemsPool.length > 10 : showSearchBox;
    }

    private getDropdownValueToDisplay(): string {
        const { separator = ' , ', multiSelect } = this.props;
        const { selectedKeys = [] } = this.state;

        if (multiSelect) {
            return selectedKeys.map(key => {
                const selectedItem = this.optionsMap.get(key);
                return selectedItem ? selectedItem.text : '';
            }).join(separator);
        }

        const item = this.optionsMap.get(selectedKeys[0]);
        return item ? item.text : '';
    }

    private _onChangeInList = (selectedKeys: React.Key[]) => {
        const { selectedKeys: selectedKeysFromProps } = this.props;
        if (selectedKeysFromProps === undefined) {
            this.setState({
                selectedKeys
            });
        }
        this._onChange(selectedKeys);
    }

    private _onSelectAllChange = () => {
        const { selectedKeys: selectedKeysFromProps } = this.props;
        const { selectedKeys } = this.state;
        const isAllKeysSelected = this.isAllSelected(selectedKeysFromProps ? ensureArray(selectedKeysFromProps) : selectedKeys);
        const updatedSelectedKeys = !isAllKeysSelected ? Array.from(this.optionsMap.keys()) : [];

        if (selectedKeysFromProps === undefined) {
            this.setState({
                selectedKeys: updatedSelectedKeys
            });
        }
        this._onChange(updatedSelectedKeys);
    }

    private _onClick = () => {
        const { showOptions } = this.state;
        const { disabled } = this.props;
        if (disabled) {
            return;
        }
        this.toggleCallout(!showOptions);
    }

    private onDismiss = () => {
        this.setState({
            showSelectAll: true
        });
        this.toggleCallout(false);
    }

    private canSelectItem = (item: IDropdownOption): boolean => !this.optionsGroupMap.has(item.key)

    private _onDropdownClose = () => {
        this.toggleCallout(false);
    }

    private _onDropdownSelection = () => {
        const { disabled } = this.props;
        if (disabled) {
            return;
        }
        this.toggleCallout(true);
    }

    private _onFilterValueChange = (value: string) => {
        const { onSearchTextChange } = this.props;
        if (onSearchTextChange) {
            onSearchTextChange(value);
        }
        this.setState({
            showSelectAll: !value,
            searchText: value,
            canFocusOptions: false
        });
    }

    private _onChange(selectedKeys: React.Key[]) {
        const { onValueChange, multiSelect } = this.props;
        if (onValueChange) {
            const isAllKeysSelected = this.isAllSelected(selectedKeys);
            const selectedItems = isAllKeysSelected ? Array.from(this.optionsMap.values()) : selectedKeys.map(key => this.optionsMap.get(key)!);
            onValueChange(selectedKeys, selectedItems);
        }
        if (!multiSelect) {
            this.toggleCallout(false);
        }
    }

    private toggleCallout(showOptions: boolean) {
        this.setState({
            showOptions,
            searchText: '',
            canFocusOptions: true
        });
    }

    private _onCloseWithoutChange = () => {
        this.toggleCallout(false);
    }

    private isAllSelected(selectedKeys: React.Key[]) {
        return selectedKeys.length === this.optionsMap.size;
    }
}
